import { ExternalLinkIcon } from '@heroicons/react/outline'
import { useWeb3React } from '@web3-react/core'
import { ArcTable, Box, IHeader, ITableRow, Link, Text } from 'components'
import { Colors } from 'constants/colors'
import { useCreditContext } from 'contexts'
import styled from 'styled-components'
import { ScoreExplanation } from 'types'
import { getBlockExplorersBaseLink, getSpacing } from 'utils'

export const ActivityHistory = () => {
  const { chainId } = useWeb3React()
  const {
    creditScore: { metadata },
  } = useCreditContext()

  const headers: IHeader[] = [
    {
      name: 'Events',
    },
    {
      name: 'Date',
      alignContent: 'right',
    },
  ]
  let rows: ITableRow[] = []

  if (metadata) {
    const explanations = getRelevantExplanations(metadata.explanations)

    if (explanations.length > 0) {
      rows = rows.concat(getRowsFromExplanations(explanations, chainId))
    }
  }
  if (!rows || rows.length === 0) {
    rows = [
      {
        cells: ['No history to show', '-'],
      },
    ]
  } else {
    // Sort the rows by date
    rows.sort((a, b) => {
      const aDate = new Date(parseInt(a.cells[1] as string))
      const bDate = new Date(parseInt(b.cells[1] as string))

      return bDate.getTime() - aDate.getTime()
    })

    // Convert the date cell to human readable format
    for (const row of rows) {
      const date = new Date(parseInt(row.cells[1] as string))
      row.cells[1] = date.toLocaleDateString('en-US', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    }
  }
  return (
    <ArcTable headers={headers} rows={rows} loading={!rows} itemsPerPage={5} rowSpacing={'s'} />
  )
}

interface ITextWithLinkProps {
  children: string | JSX.Element
  externalURL: string
}

const TextWithLink = ({ children, externalURL }: ITextWithLinkProps) => (
  <Link externalURL={externalURL}>
    <Box flexDirection="row" alignItems="end">
      <UnderlineTextHover>
        <Text weight="regular" size="xs" color="WHITE" style={{ opacity: 0.5 }}>
          {children}
        </Text>
      </UnderlineTextHover>
      <Box marginLeft={getSpacing('xs')}>
        <ExternalLinkIcon color={Colors.TEXT_DARK} width="16px" />
      </Box>
    </Box>
  </Link>
)

const UnderlineTextHover = styled.div`
  &:hover {
    text-decoration: underline;
  }
`

function getRowsFromExplanations(explanations: ScoreExplanation[], chainId: number): ITableRow[] {
  const rows: ITableRow[] = []

  for (const explanation of explanations) {
    const cells: (JSX.Element | string)[] = []

    if (explanation.transactionId) {
      cells.push(
        <TextWithLink
          key="0"
          externalURL={getBlockExplorersBaseLink(chainId) + '/tx/' + explanation.transactionId}
        >
          {explanation.explanation}
        </TextWithLink>,
      )
    } else {
      cells.push(explanation.explanation)
    }

    if (explanation.timestamp) {
      cells.push((parseInt(explanation.timestamp) * 1000).toString())
    }

    rows.push({ cells })
  }

  return rows
}

function getRelevantExplanations(explanations: ScoreExplanation[]): ScoreExplanation[] {
  const relevantExplanations: ScoreExplanation[] = []

  for (const explanation of explanations) {
    const id = explanation.id
    if (
      id.includes('activity.history') ||
      id.includes('pending') ||
      id.includes('activity.liquidation')
    ) {
      relevantExplanations.push(explanation)
    }
  }

  return relevantExplanations
}
